import { useCallback } from 'react'

import { useAuthStore } from '@/store/authStore'
import {
    safeSessionGet,
    safeLocalGet,
    safeSessionSet,
    safeLocalSet,
    cleanAuthRelatedItems,
    AUTH_STORAGE_KEYS
} from '@/utils/storage'

/**
 * Custom hook for managing storage between agent and aspirant sessions
 * Provides methods to get and set storage values based on user type
 */
export const useStorage = () => {
    const { isAgent } = useAuthStore()

    /**
     * Get a value from the appropriate storage based on user type
     */
    const getItem = useCallback(
        <T>(key: string, defaultValue?: T): T | null => {
            if (!key) {
                return defaultValue || null
            }

            // For agents, try sessionStorage first, then localStorage as fallback
            if (isAgent) {
                const sessionValue = safeSessionGet(`agent-${key}`)

                if (sessionValue) {
                    try {
                        return JSON.parse(sessionValue) as T
                    } catch (_err) {
                        // Continue to fallback if parse fails
                    }
                }
            }

            // For aspirants or as fallback, check localStorage
            const localValue = safeLocalGet(key)

            if (localValue) {
                try {
                    return JSON.parse(localValue) as T
                } catch (_err) {
                    // Fall through to default
                }
            }

            return defaultValue || null
        },
        [isAgent]
    )

    /**
     * Set a value in the appropriate storage based on user type
     */
    const setItem = useCallback(
        <T>(key: string, value: T): void => {
            if (!key) {
                return
            }

            let serializedValue: string

            try {
                serializedValue =
                    typeof value === 'string' ? value : JSON.stringify(value)
            } catch (error) {
                console.error(`Error serializing value for ${key}:`, error)

                return
            }

            if (isAgent) {
                // For agents, store in sessionStorage with prefix
                const success = safeSessionSet(`agent-${key}`, serializedValue)

                // Fallback to localStorage if sessionStorage fails
                if (!success) {
                    safeLocalSet(key, serializedValue)
                }
            } else {
                // For aspirants, store in localStorage
                safeLocalSet(key, serializedValue)
            }
        },
        [isAgent]
    )

    /**
     * Remove a value from all storages
     */
    const removeItem = useCallback((key: string): void => {
        if (!key) {
            return
        }

        try {
            sessionStorage.removeItem(`agent-${key}`)
        } catch (_err) {
            // Silently handle errors
        }

        try {
            localStorage.removeItem(key)
        } catch (_err) {
            // Silently handle errors
        }
    }, [])

    /**
     * Clear all auth-related storage when switching between agent and aspirant sessions
     */
    const clearAuthStorage = useCallback((): void => {
        // Clear standard localStorage keys
        AUTH_STORAGE_KEYS.local.forEach((key) => {
            try {
                localStorage.removeItem(key)
            } catch (_err) {
                // Silently fail individual removals
            }
        })

        // Clear sessionStorage keys
        AUTH_STORAGE_KEYS.session.forEach((key) => {
            try {
                sessionStorage.removeItem(key)
            } catch (_err) {
                // Silently fail individual removals
            }
        })

        // Clean both storage types for any other auth-related items
        cleanAuthRelatedItems(localStorage)
        cleanAuthRelatedItems(sessionStorage)
    }, [])

    return {
        getItem,
        setItem,
        removeItem,
        clearAuthStorage
    }
}
